import {
  getDayOfWeekOf,
  PeriodOfTheDay,
  WorkSchedulesFrequency,
} from '@omecare/utils';
import { addWeeks } from 'date-fns/addWeeks';
import { intlFormat } from 'date-fns/intlFormat';
import { useMemo } from 'react';

const simulateNextOccurenceStart = ({
  startAt,
  periodOfTheDay,
  frequency,
}: {
  startAt: Date;
  periodOfTheDay: PeriodOfTheDay;
  frequency: WorkSchedulesFrequency;
}) => {
  // Allday and night recur as-is every 2 weeks (no morning/afternoon
  // alternation), so their next occurrence is 2 weeks later.
  if (
    frequency === 'every-2-weeks' &&
    (periodOfTheDay === 'allday' || periodOfTheDay === 'night')
  ) {
    const nextOccurenceStartAt = addWeeks(startAt, 2);
    const nextWeekStartAtIntl = intlFormat(
      nextOccurenceStartAt,
      {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      {
        locale: 'fr',
      },
    );

    return {
      startAt: nextOccurenceStartAt,
      intl: nextWeekStartAtIntl,
    };
  }

  const nextOccurenceStartAt = addWeeks(startAt, 1);
  const nextWeekStartAtIntl = intlFormat(
    nextOccurenceStartAt,
    {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    {
      locale: 'fr',
    },
  );

  return {
    startAt: nextOccurenceStartAt,
    intl: nextWeekStartAtIntl,
  };
};

export const useWorkScheduleSlotReccurenceExampleItems = ({
  startAt,
  periodOfTheDay,
  frequency,
}: {
  startAt: Date;
  periodOfTheDay: PeriodOfTheDay;
  frequency: WorkSchedulesFrequency;
}) => {
  const exampleItems = useMemo(() => {
    const dayOfWeek = getDayOfWeekOf(startAt);

    const startAtDateIntl = intlFormat(
      startAt,
      {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      {
        locale: 'fr',
      },
    );

    const nextOccurence = simulateNextOccurenceStart({
      startAt,
      periodOfTheDay,
      frequency,
    });

    if (
      periodOfTheDay === 'allday' ||
      periodOfTheDay === 'night' ||
      frequency === 'weekly'
    ) {
      return [
        {
          day: `day-of-week.${dayOfWeek}`,
          date: startAtDateIntl,
          periodOfTheDay: `period-of-day.${periodOfTheDay}`,
        },
        {
          day: `day-of-week.${dayOfWeek}`,
          date: nextOccurence.intl,
          periodOfTheDay: `period-of-day.${periodOfTheDay}`,
        },
      ];
    } else if (periodOfTheDay === 'morning') {
      return [
        {
          day: `day-of-week.${dayOfWeek}`,
          date: startAtDateIntl,
          periodOfTheDay: 'period-of-day.morning',
        },
        {
          day: `day-of-week.${dayOfWeek}`,
          date: nextOccurence.intl,
          periodOfTheDay: 'period-of-day.afternoon',
        },
      ];
    } else if (periodOfTheDay === 'afternoon') {
      return [
        {
          day: `day-of-week.${dayOfWeek}`,
          date: startAtDateIntl,
          periodOfTheDay: 'period-of-day.afternoon',
        },
        {
          day: `day-of-week.${dayOfWeek}`,
          date: nextOccurence.intl,
          periodOfTheDay: 'period-of-day.morning',
        },
      ];
    }

    return [];
  }, [periodOfTheDay, startAt, frequency]);

  return {
    exampleItems,
  };
};
